import { useEffect, useMemo, useState } from 'react';

import { store } from '../utils';
import { getUserOrgs } from '../../api';
import { notify } from '../Notifications';
import { useUserSettings } from './useUserSettings';
import type { UserOrganizationDto } from '../../api';

const extractPermissions = (
  organizations: UserOrganizationDto[],
  selectedOrg?: string
): PermissionType[] => {
  if (!selectedOrg) return [];

  const organization = organizations?.find(o => o.id === selectedOrg);
  if (!organization) return [];

  return Array.from(
    new Set(
      ((organization.roles || []) as any)?.flatMap?.(
        (r: any) => r.permissions ?? []
      ) ?? []
    )
  ) as PermissionType[];
};

export const useSetPermissions = (
  token: string,
  loading: boolean,
  onRelogin: () => void
) => {
  const [inited, setInited] = useState(false);

  const [orgs, setOrgs] = useState<UserOrganizationDto[]>([]);
  const [selectedOrg, handleSetSelectedOrg] = useUserSettings<
    string | undefined
  >('', 'sp-outreach-selected-org');

  const setSelectedOrg = (newOrg?: string) => {
    handleSetSelectedOrg(newOrg);
  };

  useEffect(() => {
    const orgHeaders: Record<string, string> = {
      'x-ai-agent-organization-context': selectedOrg || '',
    };

    store.setHeaders(orgHeaders);
  }, [selectedOrg]);

  useEffect(
    () => {
      if (loading) return;
      if (!token) return setInited(true);

      let ignore = false;

      const processUserInfo = async () => {
        try {
          const response = await getUserOrgs();
          const organizations = response?.organizations ?? [];

          if (ignore) return;

          setOrgs(organizations);

          if (!organizations.some(o => o.id === selectedOrg)) {
            handleSetSelectedOrg(organizations[0]?.id ?? '');
          }

          setInited(true);
        } catch {
          if (ignore) return;

          setOrgs([]);
          setInited(false);
          onRelogin?.();
          notify.error('Failed to get user info');
        }
      };

      processUserInfo();

      return () => {
        ignore = true;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, loading]
  );

  const permissions = useMemo(
    () => extractPermissions(orgs, selectedOrg),
    [orgs, selectedOrg]
  );

  return {
    inited,
    orgs,
    setSelectedOrg,
    permissions,
    organization_id: selectedOrg,
  };
};
